/*
 * Copyright (c) 2025-2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import React, {useEffect, useMemo, useState} from 'react';
import {Database} from "../../APIClient.jsx";
import {Socket} from "../../Socket.js";
import {useSub} from "subscription";
import {UI} from "../../UIGenerator.jsx";
import {Nodes} from "../../Objects/Nodes.jsx";
import {Containers} from "../../Objects/Containers.jsx";
import {ResourceUsage} from "../../Objects/ResourceUsage.jsx";
import {ResourceReservations} from "../../Objects/ResourceReservations.jsx";


const num = (value) => (Number(value) || 0);

const RAM_BLOCK_GB = 0.5;
const CPU_BLOCK_CORES = 0.5;

const AVAILABLE_COLOR = "bg-gray-200";
const ALLOCATED_COLOR = "bg-green-500";
const RESERVED_COLOR = "bg-amber-500";
const HOVER_COLOR = "bg-blue-500";
const DIM_COLOR = "bg-green-200";
const DIM_RESERVED_COLOR = "bg-amber-200";


const ResourceUtilizationChart = () => {
    const [rows, setRows] = useState([]);
    const [reservedRows, setReservedRows] = useState([]);
    const [nodes, setNodes] = useState([]);
    const [containers, setContainers] = useState([]);
    const [hoveredContainer, setHoveredContainer] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                let usageData = await ResourceUsage().listAll();
                let reservedData = await ResourceReservations().list();
                let nodesData = await Nodes().listAll();
                let containersData = await Containers().listAll();
                setRows(usageData?.recordset || usageData || []);
                setReservedRows(reservedData?.recordset || reservedData || []);
                setNodes(nodesData?.recordset || nodesData || []);
                setContainers(containersData?.recordset || containersData || []);
            } catch (err) {
                console.error("Error fetching ResourceUsage data:", err);
            }
        };
        fetchData();
    }, []);

    useSub(() => Database.table("ResourceUsage", "app").onChange((change) => {
        setRows(prev => Socket.applyChange(prev, change));
    }, []), "liveResourceUsageData");

    useSub(() => Database.table("ResourceReserved", "app").onChange((change) => {
        setReservedRows(prev => Socket.applyChange(prev, change));
    }, []), "liveResourceReservedData");

    useSub(() => Database.table("Nodes", "app").onChange((change) => {
        setNodes(prev => Socket.applyChange(prev, change));
    }, []), "liveNodesDataForUsage");

    useSub(() => Database.table("Container", "app").onChange((change) => {
        setContainers(prev => Socket.applyChange(prev, change));
    }, []), "liveContainersDataForUsage");


    const nodeLabel = (nodeId) => {
        let node = nodes.find(n => n.indexKey === nodeId);
        return node?.nodeName || node?.nodeDomain || nodeId;
    };

    const containersForNode = (nodeId) => {
        return containers.filter(c => c.nodeIndexKey === nodeId);
    };

    /**
     * Build a list of block descriptors. Each block knows which container
     * it belongs to (or null for available/reserved). Blocks are laid out in order:
     * all allocated container blocks first, then reserved blocks, then available blocks.
     */
    const buildBlocks = (containersList, reservedList, total, blockSize, capacityKey) => {
        const totalBlocks = Math.round(total / blockSize);
        const blocks = [];
        let index = 0;

        for (const container of containersList) {
            const capacity = num(container?.capacity?.[capacityKey]) / blockSize;
            const containerBlocks = Math.round(capacity);
            for (let i = 0; i < containerBlocks && index < totalBlocks; i++) {
                blocks.push({container, reserved: null, index});
                index++;
            }
        }

        for (const reserved of reservedList) {
            const capacity = num(reserved?.capacity?.[capacityKey]) / blockSize;
            const reservedBlocks = Math.round(capacity);
            for (let i = 0; i < reservedBlocks && index < totalBlocks; i++) {
                blocks.push({container: null, reserved, index});
                index++;
            }
        }

        while (index < totalBlocks) {
            blocks.push({container: null, reserved: null, index});
            index++;
        }

        return blocks;
    };

    const buildCpuBlocks = (containersList, reservedList, allocatedCpu, totalCpu) =>
        buildBlocks(containersList, reservedList, totalCpu, CPU_BLOCK_CORES, "cores");

    const buildRamBlocks = (containersList, reservedList, allocatedRam, totalRam) =>
        buildBlocks(containersList, reservedList, totalRam, RAM_BLOCK_GB, "ram");

    const blockClass = (block) => {
        if (block.container) {
            if (hoveredContainer) {
                return block.container.indexKey === hoveredContainer.indexKey
                    ? HOVER_COLOR
                    : DIM_COLOR;
            }
            return ALLOCATED_COLOR;
        }
        if (block.reserved) {
            if (hoveredContainer) {
                return block.reserved.containerId === hoveredContainer.indexKey
                    ? HOVER_COLOR
                    : DIM_RESERVED_COLOR;
            }
            return RESERVED_COLOR;
        }
        return AVAILABLE_COLOR;
    };

    const sorted = useMemo(() => [...rows].sort((a, b) => {
        let aTotal = num(a?.allocated?.ram) + num(a?.available?.ram);
        let bTotal = num(b?.allocated?.ram) + num(b?.available?.ram);
        return bTotal - aTotal;
    }), [rows]);

    const hoveredContainerObj = hoveredContainer
        ? containers.find(c => c.indexKey === hoveredContainer.indexKey)
        : null;

    const openContainer = (containerId) => {
        UI.page().navigate({pathname: "/c/" + containerId});
    };

    const renderBlockRow = (blocks, unitLabel) => (
        <div className="flex flex-wrap gap-1">
            {blocks.map((block) => (
                <div
                    key={block.index}
                    className={`h-5 w-5 rounded-sm transition-colors cursor-default ${blockClass(block)}`}
                    title={block.container
                        ? `${block.container.containerName} (${block.container.indexKey})`
                        : block.reserved
                            ? `Reserved by ${block.reserved.containerId}`
                            : `Available ${unitLabel}`}
                    onMouseEnter={block.container
                        ? () => setHoveredContainer({indexKey: block.container.indexKey})
                        : block.reserved
                            ? () => setHoveredContainer({indexKey: block.reserved.containerId})
                            : undefined}
                    onClick={block.container
                        ? () => openContainer(block.container.indexKey)
                        : block.reserved
                            ? () => openContainer(block.reserved.containerId)
                            : undefined}
                />
            ))}
        </div>
    );

    return (
        <div className="p-2 space-y-4">
            {rows.length === 0 && (
                <div className="text-sm text-gray-500 p-4">No ResourceUsage data available.</div>
            )}

            {hoveredContainerObj && (
                <div
                    className="sticky top-0 z-10 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center justify-between cursor-pointer hover:bg-blue-100 transition-colors"
                    onClick={() => openContainer(hoveredContainerObj.indexKey)}
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <span className="text-sm font-semibold text-blue-900 truncate">
                            {hoveredContainerObj.containerName}
                        </span>
                        <span className="text-xs text-blue-600 font-mono truncate">
                            {hoveredContainerObj.indexKey}
                        </span>
                    </div>
                    <span className="text-xs text-blue-500 flex-shrink-0 ml-2">
                        Click to open →
                    </span>
                </div>
            )}

            {sorted.map(row => {
                const nodeId = row.nodeId || row.indexKey;
                const allocatedRam = num(row?.allocated?.ram);
                const availableRam = num(row?.available?.ram);
                const totalRam = allocatedRam + availableRam;

                const allocatedCpu = num(row?.allocated?.cores);
                const availableCpu = num(row?.available?.cores);
                const totalCpu = allocatedCpu + availableCpu;

                const actualRam = num(row?.actual?.ram);
                const actualCpu = num(row?.actual?.cores);
                const cpuPercentage = num(row?.actual?.cpu_percentage);
                const ramPercentage = num(row?.actual?.ram_percentage);

                const nodeContainers = containersForNode(nodeId);
                const nodeReserved = reservedRows.filter(r => r.nodeId === nodeId);
                const cpuBlocks = buildCpuBlocks(nodeContainers, nodeReserved, allocatedCpu, totalCpu);
                const ramBlocks = buildRamBlocks(nodeContainers, nodeReserved, allocatedRam, totalRam);

                return (
                    <div key={nodeId}
                         className="border border-gray-100 rounded-xl p-3 bg-white shadow-sm">
                        <div className="flex items-start justify-between mb-3 gap-4">
                            <h4 className="text-sm font-semibold text-gray-900 truncate pt-0.5">
                                {nodeLabel(nodeId)}
                            </h4>
                            <div className="flex flex-col items-end text-xs text-gray-500 flex-shrink-0">
                                <div className="flex gap-4">
                                    <span>STATUS {row?.status ?? "none"}</span>
                                    <span>REGION {row?.region ?? "none"}</span>
                                    <span>CPU {allocatedCpu}/{totalCpu} cores</span>
                                    <span>RAM {allocatedRam.toFixed(1)}/{totalRam.toFixed(1)} GB</span>
                                </div>
                                <div className="flex gap-4 mt-1 text-gray-400">
                                    <span>
                                        Actual: <span className="font-semibold text-gray-700">
                                            {actualCpu.toFixed(2)} cores
                                        </span>
                                        {cpuPercentage > 0 && <span> ({cpuPercentage.toFixed(1)}%)</span>}
                                    </span>
                                    <span>
                                        <span className="font-semibold text-gray-700">
                                            {actualRam.toFixed(2)} GB
                                        </span>
                                        {ramPercentage > 0 && <span> ({ramPercentage.toFixed(1)}%)</span>}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">
                                    CPU cores (1 block = {CPU_BLOCK_CORES} core)
                                </div>
                                {renderBlockRow(cpuBlocks, "CPU")}
                            </div>

                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">
                                    RAM (1 block = {RAM_BLOCK_GB} GB)
                                </div>
                                {renderBlockRow(ramBlocks, "RAM")}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default ResourceUtilizationChart;